import {useEffect, useRef} from 'react';
import isEqual from 'react-fast-compare';

import {
  getMainViewItemFromUrlParams,
  getUrlParamsFromSelectedItem,
  parseAnalyticsUrlQueryParameters,
} from 'analysis-tool/AnalysisToolUrls';
import {
  getMainViewItemForLineageNode,
  getMainViewItemForView,
  getViewForViewReference,
} from 'analysis-tool/data-view/AnalysisViewUtils';
import getDefaultMainViewItemForAnalysis from 'analysis-tool/ui/getDefaultMainViewItemForAnalysis';
import {
  AnalysisMainViewItem,
  AnalysisMainViewItemType,
  isStepAwareAnalysisItem,
  StepAwareAnalysisMainViewItemType,
} from 'analysis-tool/ui/main-view/MainViewTypes';
import {setURLParams} from 'BenchlingUtil';
import {DataFrameTableMode} from 'data-frames/table/DataFrameTableConstants';
import {dataFrameTableModeState} from 'data-frames/table/hooks/useDataFrameTableMode';
import {PipelineStepDataKind} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineGraphQL, PipelineViewLineageNode} from 'pipelines/PipelineTypes';
import {getOutputDataForView, isDataFrameView, resolveLineageStep} from 'pipelines/PipelineViewUtils';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';

import useRightSidebarItem from './useRightSidebarItem';

/**
 * Global state tracking what item is currently selected for an analysis if any.
 *
 * The state can contain either a concrete item or null. Initially, it is null and we map that to a concrete
 * item in the hook body utilizing the selectedItem. We expose that concrete item as the "selected item"
 * to the hook caller. This allows for an initial item value that is dynamic based on the analysis rather than
 * always being something like EMPTY_ANALYSIS.
 *
 * Also used by updateSelectedAnalysisItemForDelete to optimistically update state during deletions.
 */
export const selectedItemGlobalState = makeUseGlobalClientState<AnalysisMainViewItem | null>(null);

/**
 * Return the next selected `AnalysisMainViewItem` after deleting steps when the current item is a `DATA_VIEW`.
 *
 * When the selected item is a `DATA_VIEW`, and we delete a step, that selected item might become invalid. We
 * use the following logic to determine the next selected item:
 *  - If the step referenced by the selected item isn't being delete, we don't need to update the selected item,
 * so we return it.
 *  - If the step referenced by the selected item is being deleted, we select the last remaining step in the view.
 *  - If the entire view is being deleted, we select the first parent view.
 *  - If there's no remaining parent view, return `null` to allow the analysis to display it's default view.
 *
 * @returns The next selected `AnalysisMainViewItem` after deleting steps when the current item is a `DATA_VIEW`.
 * @package
 */
export function getNextSelectedAnalysisItemForView(
  analysis: PipelineGraphQL,
  selectedItem: AnalysisMainViewItem<AnalysisMainViewItemType.DATA_VIEW>,
  stepsToDelete: Set<string>
): AnalysisMainViewItem | null {
  const selectedViewReference = selectedItem.viewReference;
  const view = getViewForViewReference(analysis, selectedViewReference);

  if (view == null || view.lineage.length === 0) {
    return null;
  }

  const remainingLineageNodes = view.lineage.filter(
    ({step}: PipelineViewLineageNode) => !stepsToDelete.has(step.id)
  );

  // If the selected item isn't being deleted, we don't need to update the selected item, so return null
  if (remainingLineageNodes.some(({step}) => step.id === selectedViewReference.stepId)) {
    return selectedItem;
    // Otherwise, select the last remaining node in the view
  } else if (remainingLineageNodes.length > 0) {
    const lastRemainingNode = remainingLineageNodes[remainingLineageNodes.length - 1];
    return getMainViewItemForLineageNode(lastRemainingNode, analysis.steps);
  }

  // Otherwise, the view is getting completely deleted, so see if there's a valid parent view to select
  const firstNode = view.lineage[0];
  const firstStep = resolveLineageStep(analysis.steps, firstNode);
  if (firstStep.incomingEdges.length > 0) {
    const firstParentEdge = firstStep.incomingEdges[0];
    // A null fromStepDataIndex indicates a multi-to-multi edge where all outputs flow into the
    // input; treat it as matching any rawDataIndex, consistent with getChildViewsForView.
    const isMatchingParentNode = (node: PipelineViewLineageNode): boolean =>
      node.step.id === firstParentEdge.fromStep.id &&
      (firstParentEdge.fromStepDataIndex == null ||
        node.stepOutputReference?.rawDataIndex === firstParentEdge.fromStepDataIndex);
    const parentView = analysis.views.find((v) => {
      const terminalNode = v.lineage[v.lineage.length - 1];
      return terminalNode != null && isMatchingParentNode(terminalNode);
    });
    if (parentView != null) {
      return getMainViewItemForView(parentView, analysis.steps);
    }
  }

  return null;
}

/**
 * Return the next selected `AnalysisMainViewItem` after deleting steps when the current item is step-aware.
 *
 * If the step referenced by the selected item is being deleted, return `null` to allow the analysis to display
 * it's default view. Otherwise, we return the selected item.
 *
 * @returns The next selected `AnalysisMainViewItem` after deleting steps when the current item is step-aware.
 * @package
 */
export function getNextSelectedAnalysisItemForStepAware(
  selectedItem: AnalysisMainViewItem<StepAwareAnalysisMainViewItemType>,
  stepsToDelete: Set<string>
): AnalysisMainViewItem | null {
  if (stepsToDelete.has(selectedItem.stepId)) {
    return null;
  }

  return selectedItem;
}

/**
 * Return the `AnalysisMainViewItem` that should be selected after the delete operations.
 *
 * The selected item might reference a step that is being deleted, so it will become invalid after the
 * delete operation.
 *
 * @returns The next selected `AnalysisMainViewItem` after deleting steps.
 * @package
 */
export function getNextSelectedAnalysisItem(
  analysis: PipelineGraphQL,
  selectedItem: AnalysisMainViewItem | null,
  stepsToDelete: Set<string>
): AnalysisMainViewItem | null {
  if (selectedItem == null) {
    return selectedItem;
  } else if (selectedItem.type === AnalysisMainViewItemType.DATA_VIEW) {
    return getNextSelectedAnalysisItemForView(analysis, selectedItem, stepsToDelete);
  } else if (isStepAwareAnalysisItem(selectedItem)) {
    return getNextSelectedAnalysisItemForStepAware(selectedItem, stepsToDelete);
  }

  return selectedItem;
}

/**
 * Updates selected main view item after deleting analysis steps to maintain valid state.
 *
 * - This function reads the selected `AnalysisMainViewItem` from the client, and then it checks if it
 * needs to be updated after the delete operation.
 * - If it needs to be updated, it updates the client state and stores the previous values.
 * - If it doesn't need to be updated, it doesn't update the client state and doesn't store the previous values.
 * - If the backend deletion call fails, it rolls back the client state to the previous values if it was updated.
 */
export async function updateSelectedAnalysisItemForDelete(
  deleteFunction: () => Promise<void>,
  analysis: PipelineGraphQL,
  clientStateId: string,
  stepsToDelete: Set<string>
): Promise<void> {
  const selectedItem = selectedItemGlobalState.getState(clientStateId);
  let previousSelectedItem: AnalysisMainViewItem | null | undefined;

  const nextSelectedItem = getNextSelectedAnalysisItem(analysis, selectedItem, stepsToDelete);
  if (!isEqual(nextSelectedItem, selectedItem)) {
    selectedItemGlobalState.updateState(clientStateId, nextSelectedItem);
    previousSelectedItem = selectedItem;
  }

  try {
    await deleteFunction();
  } catch (error) {
    const selectedItem = selectedItemGlobalState.getState(clientStateId);
    if (previousSelectedItem !== undefined && !isEqual(selectedItem, previousSelectedItem)) {
      selectedItemGlobalState.updateState(clientStateId, previousSelectedItem);
    }
    throw error;
  }
}

export interface UseSelectedAnalysisItemOutput {
  selectedItem: AnalysisMainViewItem;
  onChangeSelectedItem: (newSelectedItem: AnalysisMainViewItem) => void;
}

const NOOP_TABLE_MODE_CLIENT_ID = 'noop';

/**
 * Synchronizes the browser URL with the selected analysis main view item.
 *
 * On mount: if no item is selected in global state, reads URL params and seeds initial state.
 * On item change: updates URL query params for linkable states (FLOWCHART, DATA_VIEW, and
 * step-aware output screens like SAVE_TO_RESULTS_OUTPUT, SEND_TO_DOCUMENT, REGRESSION).
 * Transient states (RUNNING, FAILED, etc.) do not update the URL.
 *
 * Loop prevention: URL is only parsed once on mount (when global state is null). After that, state is
 * the source of truth. history.replaceState does not trigger Backbone routing.
 *
 * @package
 */
function useAnalysisDeepLinkSync(
  clientStateId: string,
  analysis: PipelineGraphQL,
  selectedItem: AnalysisMainViewItem
): void {
  const hasInitializedRef = useRef(false);

  // URL → State: on first render, if global state is null, seed from URL params
  useEffect(() => {
    if (hasInitializedRef.current) {
      return;
    }
    hasInitializedRef.current = true;

    const existingState = selectedItemGlobalState.getState(clientStateId);
    if (existingState != null) {
      return;
    }

    const urlParams = parseAnalyticsUrlQueryParameters();
    const itemFromUrl = getMainViewItemFromUrlParams(urlParams, analysis);
    if (itemFromUrl != null) {
      selectedItemGlobalState.updateState(clientStateId, itemFromUrl);
      // Seed table mode state so rawTableMode reflects showChips from the URL immediately,
      // before DataFrameView mounts and the toggle-action writes to dataFrameTableModeState.
      if (itemFromUrl.type === AnalysisMainViewItemType.DATA_VIEW && itemFromUrl.showChips) {
        const seedView = getViewForViewReference(analysis, itemFromUrl.viewReference);
        if (seedView != null && isDataFrameView(seedView, analysis.steps)) {
          const seedDataFrame = getOutputDataForView(seedView, analysis.steps, PipelineStepDataKind.DATASET);
          if (seedDataFrame != null) {
            dataFrameTableModeState.updateState(seedDataFrame.id, DataFrameTableMode.RICH);
          }
        }
      }
    }
  }, [clientStateId, analysis]);

  // Subscribe to the table mode for the current DATA_VIEW so URL stays in sync when the
  // user toggles chips. For non-DATA_VIEW items (or when the view isn't found), key off
  // a dummy id so the hook call is unconditional (rules of hooks).
  const view =
    selectedItem.type === AnalysisMainViewItemType.DATA_VIEW
      ? getViewForViewReference(analysis, selectedItem.viewReference)
      : null;
  // DataFrameView keys dataFrameTableModeState by the persisted data frame's ID (the raw data ID),
  // not by getDefaultClientIdForView. Match that key here so the subscription fires on toggle.
  const dataFrameRawData =
    view != null && isDataFrameView(view, analysis.steps)
      ? getOutputDataForView(view, analysis.steps, PipelineStepDataKind.DATASET)
      : null;
  const tableModeClientId = dataFrameRawData != null ? dataFrameRawData.id : NOOP_TABLE_MODE_CLIENT_ID;
  const [rawTableMode] = dataFrameTableModeState.useGlobalClientState(tableModeClientId);

  // State → URL: update URL when selected item or table mode changes
  const isFirstRenderRef = useRef(true);
  useEffect(() => {
    // Skip the first effect run to avoid overwriting URL on initial load
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }

    const params = getUrlParamsFromSelectedItem(selectedItem);
    // Derive showChips from the actual table mode state (runtime source of truth).
    // selectedItem.showChips is only a URL→state seed on initial load.
    const showChips = rawTableMode === DataFrameTableMode.RICH ? true : null;
    // Update URL for both linkable states (non-null screen) and non-linkable states
    // (null screen) to clear stale deep-link params from the address bar.
    const newUrl = setURLParams(window.location.href, {...params, showChips});
    // Only update the URL if it actually changed to avoid unnecessary replaceState calls
    // and potential re-encoding issues with special characters in query params.
    const currentUrl = window.location.pathname + window.location.search;
    if (newUrl !== currentUrl) {
      window.history.replaceState(window.history.state, '', newUrl);
    }
  }, [selectedItem, rawTableMode]);
}

/**
 * Hook to capture an item selected for display in the main view of the Analysis UI.
 */
export default function useSelectedAnalysisItem(
  clientStateId: string,
  analysis: PipelineGraphQL
): UseSelectedAnalysisItemOutput {
  const [maybeSelectedItem, setSelectedItem] = selectedItemGlobalState.useGlobalClientState(clientStateId);

  let selectedItem = maybeSelectedItem ?? getDefaultMainViewItemForAnalysis(analysis);

  if (
    selectedItem.type === AnalysisMainViewItemType.DATA_VIEW &&
    getViewForViewReference(analysis, selectedItem.viewReference) == null
  ) {
    selectedItem = getDefaultMainViewItemForAnalysis(analysis);
  }

  const {clearRightSidebarItem} = useRightSidebarItem(clientStateId);

  useAnalysisDeepLinkSync(clientStateId, analysis, selectedItem);

  return {
    selectedItem,
    onChangeSelectedItem: (item: AnalysisMainViewItem | null) => {
      setSelectedItem(item);
      clearRightSidebarItem();
    },
  };
}
